import { IconSvgElement } from "@hugeicons/react";
import {
  Analytics01Icon,
  Appointment02Icon,
  Calendar04Icon,
  ChartLineData02Icon,
  FilterIcon,
  LaptopSettingsIcon,
  SquareLock02Icon,
  Wallet01Icon,
  Call02Icon,
  ChartUpIcon,
  DashboardSquareSettingIcon,
  DocumentValidationIcon,
  Door02Icon,
  File02Icon,
  Folder01Icon,
  Notification01Icon,
  PieChart01Icon,
  PlazaIcon,
  UserCircleIcon,
  IdentityCardIcon,
  FlowIcon,
  CheckmarkSquare02Icon,
  InboxIcon,
  Shield01Icon,
  SmartPhone01Icon,
  Mail01Icon
} from "@hugeicons-pro/core-stroke-rounded";

export enum Links {
  CREDIT_IMPACT = "Credit impact",
  RENTERS = "Renters",
  RENT_REPORTING = "Rent reporting",
  IDENTITY_THEFT_PROTECTION = "Identity theft protection",
  APPLICATIONS = "Applications",
  SCREENING_OUTCOMES = "Screening outcomes",
  SCREENING_SETTINGS = "Screening settings",
  ANALYTICS = "Analytics",
  OWNER_REPORTS = "Owner reports",
  PROPERTIES = "Properties",
  UNITS = "Units",
  PROPERTY_GROUPS = "Property groups",
  NOTIFICATIONS = "Notifications",
  TASKS = "Tasks",
  INBOX = "Inbox",
  CONTACTS = "Contacts",
  WORKFLOWS = "Workflows",
  COMMUNICATION_CAMPAIGNS = "Campaigns",
  PHONE_NUMBERS = "Phone numbers",
  CALLS = "Calls"
}

export enum NavItemId {
  NOTIFICATIONS = "notifications",
  INBOX = "inbox",
  TASKS = "tasks",
  CONTACTS = "contacts",
  UNITS = "units",
  PROPERTIES = "properties",
  PROPERTY_GROUPS = "property_groups",
  WORKFLOWS = "workflows",
  CAMPAIGNS = "campaigns",
  PHONE_NUMBERS = "phone_numbers",
  CALLS = "calls",
  ANALYTICS = "analytics",
  OWNER_REPORTS = "owner_reports",
  LEADS = "leads",
  LISTING_SETTINGS = "listing_settings",
  LOCKBOXES = "lockboxes",
  SHOWINGS = "showings",
  MARKET_RENT = "market_rent",
  APPLICATIONS = "applications",
  SCREENING_OUTCOMES = "screening_outcomes",
  SCREENING_SETTINGS = "screening_settings",
  OUTSTANDING_BALANCE = "outstanding_balance",
  RENTERS = "renters",
  RENT_REPORTING = "rent_reporting",
  IDENTITY_THEFT_PROTECTION = "identity_theft_protection",
  CREDIT_IMPACT = "credit_impact"
}

export enum NavSectionId {
  GLOBAL = "global",
  CRM = "crm",
  SCREEN = "screen",
  COLLECTIONS = "collections",
  REPORT = "report"
}

export interface NavLinkConfig {
  id: NavItemId;
  name: string;
  to: string;
  Icon: IconSvgElement;
  strokeWidth?: number;
  isBeta?: boolean;
  isNew?: boolean;
  newUntil?: string;
}

export const boomReportLinks: (NavLinkConfig & { permission: string })[] = [
  {
    id: NavItemId.RENTERS,
    name: Links.RENTERS,
    to: "/renters",
    Icon: UserCircleIcon,
    permission: "canViewRentersList"
  },
  {
    id: NavItemId.RENT_REPORTING,
    name: Links.RENT_REPORTING,
    to: "/rent-reporting",
    Icon: Appointment02Icon,
    permission: "canViewRentReporting"
  },
  {
    id: NavItemId.IDENTITY_THEFT_PROTECTION,
    name: Links.IDENTITY_THEFT_PROTECTION,
    to: "/identity-theft-protection",
    Icon: Shield01Icon,
    permission: "canViewRentReporting"
  },
  {
    id: NavItemId.CREDIT_IMPACT,
    name: Links.CREDIT_IMPACT,
    to: "/credit-impact",
    Icon: ChartUpIcon,
    permission: "canViewCreditReporting",
    strokeWidth: 1.5
  }
];

export const globalLinks: NavLinkConfig[] = [
  {
    id: NavItemId.NOTIFICATIONS,
    name: Links.NOTIFICATIONS,
    to: "/notifications",
    Icon: Notification01Icon
  },
  { id: NavItemId.INBOX, name: Links.INBOX, to: "/inbox", Icon: InboxIcon },
  {
    id: NavItemId.TASKS,
    name: Links.TASKS,
    to: "/tasks",
    Icon: CheckmarkSquare02Icon
  },
  {
    id: NavItemId.CONTACTS,
    name: Links.CONTACTS,
    to: "/contacts",
    Icon: IdentityCardIcon
  },
  { id: NavItemId.UNITS, name: Links.UNITS, to: "/units", Icon: Door02Icon },
  {
    id: NavItemId.PROPERTIES,
    name: Links.PROPERTIES,
    to: "/properties",
    Icon: PlazaIcon
  },
  {
    id: NavItemId.PROPERTY_GROUPS,
    name: Links.PROPERTY_GROUPS,
    to: "/property-groups",
    Icon: Folder01Icon
  },
  {
    id: NavItemId.WORKFLOWS,
    name: Links.WORKFLOWS,
    to: "/workflows",
    Icon: FlowIcon
  },
  {
    id: NavItemId.CAMPAIGNS,
    name: Links.COMMUNICATION_CAMPAIGNS,
    to: "/campaigns",
    Icon: Mail01Icon
  },
  {
    id: NavItemId.PHONE_NUMBERS,
    name: Links.PHONE_NUMBERS,
    to: "/phone-numbers",
    Icon: SmartPhone01Icon
  },
  {
    id: NavItemId.CALLS,
    name: Links.CALLS,
    to: "/calls",
    Icon: Call02Icon
  },
  {
    id: NavItemId.ANALYTICS,
    name: Links.ANALYTICS,
    to: "/analytics",
    Icon: PieChart01Icon
  },
  {
    id: NavItemId.OWNER_REPORTS,
    name: Links.OWNER_REPORTS,
    to: "/owner-reports",
    Icon: Analytics01Icon,
    isNew: true,
    newUntil: "2026-11-28"
  }
];

export const boomScreenLinks: NavLinkConfig[] = [
  {
    id: NavItemId.APPLICATIONS,
    name: Links.APPLICATIONS,
    to: "/applications",
    Icon: File02Icon
  },
  {
    id: NavItemId.SCREENING_OUTCOMES,
    name: Links.SCREENING_OUTCOMES,
    to: "/outcomes",
    Icon: DocumentValidationIcon
  },
  {
    id: NavItemId.SCREENING_SETTINGS,
    name: Links.SCREENING_SETTINGS,
    to: "/verification-templates",
    Icon: DashboardSquareSettingIcon
  }
];

export const boomCrmLinks: NavLinkConfig[] = [
  { id: NavItemId.LEADS, name: "Leads", to: "/leads", Icon: FilterIcon },
  {
    id: NavItemId.LISTING_SETTINGS,
    name: "Listing settings",
    to: "/listing-settings",
    Icon: LaptopSettingsIcon
  },
  {
    id: NavItemId.LOCKBOXES,
    name: "Lockboxes",
    to: "/lockboxes",
    Icon: SquareLock02Icon
  },
  {
    id: NavItemId.SHOWINGS,
    name: "Showings",
    to: "/showings",
    Icon: Calendar04Icon
  },
  {
    id: NavItemId.MARKET_RENT,
    name: "Market rent",
    to: "/market-rent",
    Icon: ChartLineData02Icon,
    isBeta: true
  }
];

export const collectionsLinks: NavLinkConfig[] = [
  {
    id: NavItemId.OUTSTANDING_BALANCE,
    name: "Outstanding balance",
    to: "/collections/outstanding-balance",
    Icon: Wallet01Icon
  }
];

export const NAV_SECTION_CLASS_NAME: Record<NavSectionId, string> = {
  [NavSectionId.GLOBAL]: "mb-3 px-4 relative",
  [NavSectionId.CRM]: "hideScroll mb-3 pl-4 pr-5",
  [NavSectionId.SCREEN]: "hideScroll mb-3 px-4",
  [NavSectionId.COLLECTIONS]: "hideScroll mb-3 px-4",
  [NavSectionId.REPORT]: "mb-6 border-solid border-alpha-40 px-4"
};
